import { ArrowRight, Activity, CalendarCheck } from "lucide-react";

// ── Deterministic pseudo-random helpers ──
function hashString(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = (h << 5) - h + s.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h);
}

const WEEKDAY_JP = ["日", "月", "火", "水", "木", "金", "土"];

function getLiveStatus() {
    const now = new Date();
    const dayMs = 1000 * 60 * 60 * 24;
    const daysSinceEpoch = Math.floor(now.getTime() / dayMs);

    // 現在対応中アプリ: 0-4, 3日ごとに更新
    const periodKey = Math.floor(daysSinceEpoch / 3);
    const inProgress = hashString(`in-progress-${periodKey}`) % 5;

    // 最短ご相談アポ可能日: 1-3日後（土曜は除外）、毎日更新
    const offset = (hashString(`appointment-${daysSinceEpoch}`) % 3) + 1;
    const target = new Date(now);
    target.setHours(0, 0, 0, 0);
    target.setDate(target.getDate() + offset);
    if (target.getDay() === 6) {
        // 土曜なら日曜にスライド
        target.setDate(target.getDate() + 1);
    }

    return {
        inProgress,
        target,
        targetLabel: `${target.getMonth() + 1}月${target.getDate()}日(${WEEKDAY_JP[target.getDay()]})`,
    };
}

const status = getLiveStatus();

export default function Statement() {
    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="statement"
            data-testid="statement-section"
            className="relative bg-white"
        >
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24 sm:py-32 text-center">
                <h1
                    data-testid="statement-title"
                    className="font-serif-jp text-4xl sm:text-5xl lg:text-[4.25rem] xl:text-[4.75rem] font-bold tracking-[0.005em] leading-[1.3] text-[#1F2A23] subpixel-antialiased"
                >
                    世界をもっと、
                    <br />
                    <span className="text-[#5A9C3A]">やさしく</span>、
                    <span className="text-[#5A9C3A]">まるく</span>、しよう。
                </h1>

                <p
                    data-testid="statement-subtitle"
                    className="mt-10 text-base sm:text-lg text-[#4B5A4E] leading-[2] max-w-3xl mx-auto subpixel-antialiased"
                >
                    受発注・売上・経費・シフト・請求書作成まで。
                    <br className="hidden sm:block" />
                    毎日の業務に合わせて、
                    <span className="font-semibold text-[#1F2A23]">使いやすい社内システム</span>
                    を設計・開発します。
                </p>

                <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
                    <button
                        data-testid="statement-cta-contact"
                        onClick={() => scrollTo("contact")}
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#1F2A23] text-white text-sm font-semibold tracking-wide shadow-soft hover:bg-[#2F5F1A] transition-colors"
                    >
                        無料で相談する
                        <ArrowRight size={16} strokeWidth={2.25} />
                    </button>
                    <button
                        data-testid="statement-cta-works"
                        onClick={() => scrollTo("capabilities")}
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#1F2A23] text-sm font-semibold tracking-wide border border-[#D7E4C8] hover:bg-[#F0F7E8] transition-colors"
                    >
                        できることを見る
                    </button>
                </div>

                {/* Static stats */}
                <dl
                    data-testid="statement-stats"
                    className="mt-20 grid grid-cols-3 gap-px max-w-2xl mx-auto bg-[#E6EFD8] border border-[#E6EFD8] rounded-2xl overflow-hidden"
                >
                    {[
                        { k: "自社サービス", v: "8+" },
                        { k: "導入実績", v: "100+" },
                        { k: "最短納期", v: "3日" },
                    ].map((s) => (
                        <div
                            key={s.k}
                            data-testid={`statement-stat-${s.k}`}
                            className="bg-white p-5 sm:p-6"
                        >
                            <dt className="text-[10px] font-semibold tracking-[0.18em] text-[#7A8A7C] uppercase">
                                {s.k}
                            </dt>
                            <dd className="mt-1 font-serif-jp text-2xl sm:text-3xl font-bold text-[#1F2A23] tracking-wide">
                                {s.v}
                            </dd>
                        </div>
                    ))}
                </dl>

                {/* Live status message box */}
                <div
                    data-testid="live-status"
                    className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto"
                >
                    <div
                        data-testid="live-in-progress"
                        className="flex items-center gap-3 rounded-2xl border border-[#E6EFD8] bg-[#FAFCF5] px-5 py-4 text-left"
                    >
                        <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#E6F2D6] text-[#5A9C3A] shrink-0">
                            <Activity size={18} strokeWidth={2.25} />
                            {status.inProgress > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#5A9C3A] ring-2 ring-white animate-pulse" />
                            )}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-semibold tracking-[0.18em] text-[#7A8A7C] uppercase">
                                現在対応中アプリ
                            </div>
                            <div className="mt-0.5 font-serif-jp text-lg sm:text-xl font-bold text-[#1F2A23]">
                                {status.inProgress}
                                <span className="ml-1 text-sm font-medium text-[#4B5A4E]">件</span>
                            </div>
                        </div>
                    </div>

                    <div
                        data-testid="live-appointment"
                        className="flex items-center gap-3 rounded-2xl border border-[#E6EFD8] bg-[#FAFCF5] px-5 py-4 text-left"
                    >
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#E6F2D6] text-[#5A9C3A] shrink-0">
                            <CalendarCheck size={18} strokeWidth={2.25} />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-semibold tracking-[0.18em] text-[#7A8A7C] uppercase">
                                最短ご相談アポ可能日
                            </div>
                            <div className="mt-0.5 font-serif-jp text-lg sm:text-xl font-bold text-[#1F2A23]">
                                {status.targetLabel}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
